import React, { useState, useRef, useEffect } from 'react';
import { Save, MessageSquare, BarChart3, FileText, LogOut, ZoomIn, ZoomOut, FolderOpen, Share2, Clock } from 'lucide-react';
import { RoleBadge } from './useFlowchartAccess';
import { useT } from './i18n/index';
import LanguageSwitcher from './LanguageSwitcher';
import type { UserRole } from './types';
import type { EndTimeDisplay } from './useUserFlowchartViewSettings';
// ── Types ─────────────────────────────────────────────────────────────────

interface OnlineUser {
  userId: string;
  email: string;
  color: string;
}

export interface CanvasTopbarProps {
  title: string;
  onTitleChange: (t: string) => void;

  onNew: () => void;
  onOpen: () => void;
  onSave: () => void;
  isSaving: boolean;

  onShowHistory: () => void;
  onShowShare: () => void;
  currentId: string | null;
  userRole: UserRole | null;

  showHelpPanel: boolean;
  onToggleHelpPanel: () => void;

  user: { email?: string } | null;
  onSignOut: () => void;
  onlineUsers: OnlineUser[];

  // Replaces the old simple/advanced mode switch: duration/assignee fields on a box
  // are editable either by selecting that box, or — for every box at once, matching
  // the old "advanced" mode's effect — by turning this on.
  viewAllFields: boolean;
  onToggleViewAllFields: () => void;

  durationUnit: string;
  onDurationUnitChange: (u: string) => void;

  endTimeDisplay: EndTimeDisplay;
  onEndTimeDisplayChange: (v: EndTimeDisplay) => void;

  showNotes: boolean;
  onToggleNotes: () => void;
  showSubitemBadge: boolean;
  onToggleSubitemBadge: () => void;
  showUidBadge: boolean;
  onToggleUidBadge: () => void;
  showAreas: boolean;
  onToggleAreas: () => void;

  connectActive: boolean;
  onToggleConnect: () => void;

  onShowPlanSettings: () => void;
  onShowGantt: () => void;
  currentProjectId: string | null;
  currentProjectName: string | null;
  onShowProjectGantt: () => void;

  zoom: number;
  onZoomIn: () => void;
  onZoomOut: () => void;
}

// ── Component ─────────────────────────────────────────────────────────────

export default function CanvasTopbar(props: CanvasTopbarProps) {
  const {
    title, onTitleChange,
    onNew, onOpen, onSave, isSaving,
    onShowHistory, onShowShare, currentId, userRole,
    showHelpPanel, onToggleHelpPanel,
    user, onSignOut, onlineUsers,
    viewAllFields, onToggleViewAllFields,
    durationUnit, onDurationUnitChange,
    endTimeDisplay, onEndTimeDisplayChange,
    showNotes, onToggleNotes,
    showSubitemBadge, onToggleSubitemBadge,
    showUidBadge, onToggleUidBadge,
    showAreas, onToggleAreas,
    connectActive, onToggleConnect,
    onShowPlanSettings, onShowGantt, currentProjectId, currentProjectName, onShowProjectGantt,
    zoom, onZoomIn, onZoomOut,
  } = props;

  const { t } = useT();
  // Purely local UI state — nothing outside this one dropdown needs to know
  // whether it's open, unlike showNotes/showSubitemBadge/showUidBadge themselves
  // (consumed by CanvasBox/export/etc, so those stay controlled via props).
  // position: 'fixed' + a measured anchor (not 'absolute' inside this scrollable
  // toolbar) — same pattern as LanguageSwitcher.tsx, needed because this toolbar's
  // own overflowX:'auto' clips any absolutely-positioned descendant vertically too
  // (an "overflow-x: auto" ancestor computes overflow-y to 'auto' as well per the
  // CSS spec, not just 'visible'), which was cutting the popup off behind the canvas.
  const [showDisplayMenu, setShowDisplayMenu] = useState(false);
  const displayMenuBtnRef = useRef<HTMLButtonElement>(null);
  const displayMenuPopupRef = useRef<HTMLDivElement>(null);
  const [displayMenuPos, setDisplayMenuPos] = useState<{ top: number; left: number } | null>(null);

  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (
        displayMenuPopupRef.current && !displayMenuPopupRef.current.contains(e.target as Node) &&
        displayMenuBtnRef.current && !displayMenuBtnRef.current.contains(e.target as Node)
      ) setShowDisplayMenu(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  const toggleDisplayMenu = () => {
    if (displayMenuBtnRef.current) {
      const r = displayMenuBtnRef.current.getBoundingClientRect();
      setDisplayMenuPos({ top: r.bottom + 6, left: r.left });
    }
    setShowDisplayMenu(v => !v);
  };

  // Same button+measured-popup pattern as showDisplayMenu above, for the
  // Day/Date/Year/Time checkboxes shown only when endTimeDisplay.mode === 'date'.
  const [showDateOptionsMenu, setShowDateOptionsMenu] = useState(false);
  const dateOptionsBtnRef = useRef<HTMLButtonElement>(null);
  const dateOptionsPopupRef = useRef<HTMLDivElement>(null);
  const [dateOptionsMenuPos, setDateOptionsMenuPos] = useState<{ top: number; left: number } | null>(null);

  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (
        dateOptionsPopupRef.current && !dateOptionsPopupRef.current.contains(e.target as Node) &&
        dateOptionsBtnRef.current && !dateOptionsBtnRef.current.contains(e.target as Node)
      ) setShowDateOptionsMenu(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  const toggleDateOptionsMenu = () => {
    if (dateOptionsBtnRef.current) {
      const r = dateOptionsBtnRef.current.getBoundingClientRect();
      setDateOptionsMenuPos({ top: r.bottom + 6, left: r.left });
    }
    setShowDateOptionsMenu(v => !v);
  };

  // Toggle one Day/Date/Year/Time flag — ignored if it would leave all 4 false,
  // so the start/end display is never blank.
  const toggleEndTimeDisplayFlag = (flag: 'day' | 'date' | 'year' | 'time') => {
    const next = { ...endTimeDisplay, [flag]: !endTimeDisplay[flag] };
    if (!next.day && !next.date && !next.year && !next.time) return;
    onEndTimeDisplayChange(next);
  };

  return (
    <div className="shadow" style={{ overflowX: 'auto', flexShrink: 0, background: '#EDE7E3' }}>
    <div className="px-4 py-2 flex items-center justify-between gap-2" style={{ minWidth: 'max-content' }}>

      {/* Left: General items */}
      <div className="flex items-center gap-1.5">
        <div style={{ marginRight: '16px' }}>
          <h1 className="text-lg font-bold text-gray-800">MindYourPlan</h1>
          <p style={{ fontSize: 10, color: '#6b7280', margin: 0, lineHeight: 1 }}>{t('appSubtitleFlow')}</p>
        </div>

        <input
          data-tour="title"
          value={title}
          onChange={(e) => onTitleChange(e.target.value)}
          className="px-2 py-1 border rounded text-sm w-32"
          placeholder={t('projectPlaceholder')}
        />

        <button
          data-tour="btn-new"
          onClick={onNew}
          className="text-white px-2 py-1 rounded text-xs flex items-center gap-1 transition-all"
          style={{ background: 'linear-gradient(to bottom, #6b7280, #4b5563)', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}
          onMouseOver={(e) => { e.currentTarget.style.boxShadow = '0 6px 8px rgba(0,0,0,0.15)'; e.currentTarget.style.background = 'linear-gradient(to bottom, #4b5563, #374151)'; }}
          onMouseOut={(e) => { e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)'; e.currentTarget.style.background = 'linear-gradient(to bottom, #6b7280, #4b5563)'; }}
        >
          <FileText size={13} />
          {t('new')}
        </button>

        <button
          data-tour="btn-open"
          onClick={onOpen}
          className="text-white px-2 py-1 rounded text-xs flex items-center gap-1 transition-all"
          style={{ background: 'linear-gradient(to bottom, #82C0CC, #489FB5)', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}
          onMouseOver={(e) => { e.currentTarget.style.boxShadow = '0 6px 8px rgba(0,0,0,0.15)'; e.currentTarget.style.background = 'linear-gradient(to bottom, #489FB5, #16697A)'; }}
          onMouseOut={(e) => { e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)'; e.currentTarget.style.background = 'linear-gradient(to bottom, #82C0CC, #489FB5)'; }}
        >
          <FolderOpen size={13} />
          {t('open')}
        </button>

        <button
          data-tour="btn-save"
          onClick={onSave}
          disabled={isSaving}
          className="text-white px-2 py-1 rounded text-xs flex items-center gap-1 transition-all"
          style={{ background: 'linear-gradient(to bottom, #489FB5, #16697A)', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}
          onMouseOver={(e) => { if (!isSaving) { e.currentTarget.style.boxShadow = '0 6px 8px rgba(0,0,0,0.15)'; e.currentTarget.style.background = 'linear-gradient(to bottom, #16697A, #0d4f5d)'; }}}
          onMouseOut={(e) => { e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)'; e.currentTarget.style.background = 'linear-gradient(to bottom, #489FB5, #16697A)'; }}
        >
          <Save size={13} />
          {isSaving ? t('saving') : t('save')}
        </button>

        <button
          onClick={onShowHistory}
          disabled={!currentId}
          className="text-white px-2 py-1 rounded text-xs flex items-center gap-1 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
          style={{ background: 'linear-gradient(to bottom, #FFA62B, #e8931a)', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}
          onMouseOver={(e) => { if (currentId) { e.currentTarget.style.boxShadow = '0 6px 8px rgba(0,0,0,0.15)'; e.currentTarget.style.background = 'linear-gradient(to bottom, #e8931a, #d17d0f)'; }}}
          onMouseOut={(e) => { e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)'; e.currentTarget.style.background = 'linear-gradient(to bottom, #FFA62B, #e8931a)'; }}
          title={!currentId ? t('historyDisabledHint') : t('historyHint')}
        >
          <Clock size={13} />
          {t('history')}
        </button>

        <button
          data-tour="btn-share"
          onClick={onShowShare}
          disabled={!currentId}
          className="text-white px-2 py-1 rounded text-xs flex items-center gap-1 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
          style={{ background: 'linear-gradient(to bottom, #82C0CC, #489FB5)', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}
          onMouseOver={(e) => { if (currentId) { e.currentTarget.style.boxShadow = '0 6px 8px rgba(0,0,0,0.15)'; e.currentTarget.style.background = 'linear-gradient(to bottom, #489FB5, #16697A)'; }}}
          onMouseOut={(e) => { e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)'; e.currentTarget.style.background = 'linear-gradient(to bottom, #82C0CC, #489FB5)'; }}
          title={!currentId ? t('shareDisabledHint') : t('shareHint')}
        >
          <Share2 size={13} />
          {t('share')}
        </button>

        <RoleBadge currentId={currentId} userRole={userRole} />

        <button
          onClick={onToggleHelpPanel}
          className="text-gray-500 hover:text-blue-500 px-2 py-1 rounded text-xs font-bold border border-gray-300 hover:border-blue-400 transition-all"
          style={{ height: '28px', minHeight: '28px', flexShrink: 0, background: showHelpPanel ? '#eff6ff' : undefined, borderColor: showHelpPanel ? '#93c5fd' : undefined, color: showHelpPanel ? '#2563eb' : undefined }}
          title={t('controls')}
        >
          ?
        </button>

        {/* User avatar */}
        {user?.email && (() => {
          const parts = user.email.split('@')[0].split(/[.\-_]/);
          const initials = parts.length >= 2
            ? (parts[0][0] + parts[1][0]).toUpperCase()
            : user.email.slice(0, 2).toUpperCase();
          return (
            <div title={user.email} style={{ width: 28, height: 28, borderRadius: '50%', background: 'linear-gradient(135deg, #10b981, #059669)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, flexShrink: 0, boxShadow: '0 2px 6px rgba(0,0,0,0.18)', cursor: 'default', border: '2px solid white', letterSpacing: '0.02em' }}>
              {initials}
            </div>
          );
        })()}

        <button
          onClick={onSignOut}
          className="text-white px-2 py-1 rounded text-xs flex items-center gap-1 transition-all"
          style={{ background: 'linear-gradient(to bottom, #fca5a5, #f08080)', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', height: '28px', minHeight: '28px', maxHeight: '28px', flexShrink: 0 }}
          onMouseOver={(e) => { e.currentTarget.style.boxShadow = '0 6px 8px rgba(0,0,0,0.15)'; e.currentTarget.style.background = 'linear-gradient(to bottom, #f08080, #e06060)'; }}
          onMouseOut={(e) => { e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)'; e.currentTarget.style.background = 'linear-gradient(to bottom, #fca5a5, #f08080)'; }}
        >
          <LogOut size={13} />
          {t('logOut')}
        </button>

        {/* Online user avatars */}
        <div className="flex items-center gap-2 ml-2">
          {onlineUsers.length > 0 && (
            <div className="flex items-center gap-1" title={t('usersOnline', { count: onlineUsers.length })}>
              {onlineUsers.slice(0, 4).map((u) => (
                <div
                  key={u.userId}
                  title={u.email}
                  style={{ width: 22, height: 22, borderRadius: '50%', background: u.color, border: '2px solid white', boxShadow: '0 0 0 1.5px ' + u.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, color: 'white', flexShrink: 0 }}
                >
                  {u.email.substring(0, 1).toUpperCase()}
                </div>
              ))}
              {onlineUsers.length > 4 && (
                <span style={{ fontSize: 11, color: '#6b7280', fontWeight: 600 }}>+{onlineUsers.length - 4}</span>
              )}
            </div>
          )}
        </div>

        <div className="h-6 w-px bg-gray-300"></div>
      </div>

      {/* Middle: start/end display options — always visible now; the old
          simple/advanced switch that used to gate these is gone (see
          viewAllFields's own comment for what replaced it).
          Two-step control: first pick Date vs Offset, then a second control
          refines that choice — hours/days/weeks (this IS durationUnit, the
          same shared scheduling setting as before, just only shown here while
          relevant) for Offset, or Day/Date/Year/Time checkboxes for Date. */}
      <div className="flex items-center gap-2">
        <span className="text-xs font-medium text-gray-600">{t('showStartEnd')}</span>
        <div className="flex items-center border rounded px-2 py-1" style={{ background: 'linear-gradient(to bottom, #f9fafb, #f3f4f6)', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
          <select
            value={endTimeDisplay.mode}
            onChange={(e) => onEndTimeDisplayChange({ ...endTimeDisplay, mode: e.target.value as 'date' | 'offset' })}
            className="px-1 py-0.5 rounded text-xs font-medium bg-transparent border-none text-gray-700 cursor-pointer"
            style={{ outline: 'none' }}
          >
            <option value="date">{t('startEndModeDate')}</option>
            <option value="offset">{t('startEndModeOffset')}</option>
          </select>
        </div>

        {endTimeDisplay.mode === 'offset' ? (
          <div className="flex items-center border rounded px-2 py-1" style={{ background: 'linear-gradient(to bottom, #f9fafb, #f3f4f6)', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
            <select
              value={durationUnit}
              onChange={(e) => onDurationUnitChange(e.target.value)}
              className="px-1 py-0.5 rounded text-xs font-medium bg-transparent border-none text-gray-700 cursor-pointer"
              style={{ outline: 'none' }}
            >
              <option value="hours">{t('hours')}</option>
              <option value="days">{t('days')}</option>
              <option value="weeks">{t('weeks')}</option>
            </select>
          </div>
        ) : (
          <>
            <button
              ref={dateOptionsBtnRef}
              onClick={toggleDateOptionsMenu}
              className="flex items-center gap-1 px-2 py-1 border rounded text-xs font-medium bg-gray-50 hover:bg-gray-100"
              style={showDateOptionsMenu ? { borderColor: '#16697A', background: '#e6f4f5', color: '#16697A' } : {}}
            >
              {[
                endTimeDisplay.day && t('endDisplayDay'),
                endTimeDisplay.date && t('endDisplayDate'),
                endTimeDisplay.year && t('endDisplayYear'),
                endTimeDisplay.time && t('endDisplayTime'),
              ].filter(Boolean).join(', ')} ▾
            </button>
            {showDateOptionsMenu && dateOptionsMenuPos && (
              <div
                ref={dateOptionsPopupRef}
                style={{ position: 'fixed', top: dateOptionsMenuPos.top, left: dateOptionsMenuPos.left, background: 'white', border: '1px solid #e5e7eb', borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.15)', zIndex: 2000, padding: 8, display: 'flex', flexDirection: 'column', gap: 6, minWidth: 110 }}
              >
                <label className="flex items-center gap-2 text-xs cursor-pointer select-none">
                  <input type="checkbox" checked={endTimeDisplay.day} onChange={() => toggleEndTimeDisplayFlag('day')} className="cursor-pointer" />
                  {t('endDisplayDay')}
                </label>
                <label className="flex items-center gap-2 text-xs cursor-pointer select-none">
                  <input type="checkbox" checked={endTimeDisplay.date} onChange={() => toggleEndTimeDisplayFlag('date')} className="cursor-pointer" />
                  {t('endDisplayDate')}
                </label>
                <label className="flex items-center gap-2 text-xs cursor-pointer select-none">
                  <input type="checkbox" checked={endTimeDisplay.year} onChange={() => toggleEndTimeDisplayFlag('year')} className="cursor-pointer" />
                  {t('endDisplayYear')}
                </label>
                <label className="flex items-center gap-2 text-xs cursor-pointer select-none">
                  <input type="checkbox" checked={endTimeDisplay.time} onChange={() => toggleEndTimeDisplayFlag('time')} className="cursor-pointer" />
                  {t('endDisplayTime')}
                </label>
              </div>
            )}
          </>
        )}
      </div>

      {/* Right: Canvas-specific controls + zoom */}
      <div className="flex items-center gap-1.5">

        {/* Show display-options dropdown (comments / subitem count / UID badges) —
            replaces the old single "show notes" switch. Popup open/close state is
            local to this component (nothing else needs to know); the three
            underlying values are still controlled via props since CanvasBox and the
            export pipeline both need them. */}
        <>
          <button
            ref={displayMenuBtnRef}
            onClick={toggleDisplayMenu}
            // Reuses the old simple/advanced toggle's tour target — that step now
            // points here and describes "View all fields" instead (see its i18n
            // strings), rather than needing a TourOverlay.tsx step-list change.
            data-tour="mode-switch"
            className="flex items-center gap-1.5 px-3 py-1 border rounded text-xs font-semibold bg-gray-50 hover:bg-gray-100"
            style={showDisplayMenu ? { borderColor: '#16697A', background: '#e6f4f5', color: '#16697A' } : {}}
          >
            <MessageSquare size={14} color={(!showNotes || !showSubitemBadge || !showUidBadge || !showAreas) ? '#FFA62B' : '#6b7280'} />
            {t('showOptionsBtn')} ▾
          </button>
          {showDisplayMenu && displayMenuPos && (
            <div
              ref={displayMenuPopupRef}
              style={{ position: 'fixed', top: displayMenuPos.top, left: displayMenuPos.left, background: 'white', border: '1px solid #e5e7eb', borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.15)', zIndex: 2000, padding: 8, display: 'flex', flexDirection: 'column', gap: 6, minWidth: 150 }}
            >
              <label className="flex items-center gap-2 text-xs cursor-pointer select-none">
                <input type="checkbox" checked={showNotes} onChange={onToggleNotes} className="cursor-pointer" />
                {t('showComments')}
              </label>
              <label className="flex items-center gap-2 text-xs cursor-pointer select-none">
                <input type="checkbox" checked={showSubitemBadge} onChange={onToggleSubitemBadge} className="cursor-pointer" />
                {t('showSubitemBadgesLabel')}
              </label>
              <label className="flex items-center gap-2 text-xs cursor-pointer select-none">
                <input type="checkbox" checked={showUidBadge} onChange={onToggleUidBadge} className="cursor-pointer" />
                {t('uidLabel')}
              </label>
              <label className="flex items-center gap-2 text-xs cursor-pointer select-none">
                <input type="checkbox" checked={showAreas} onChange={onToggleAreas} className="cursor-pointer" />
                {t('area')}
              </label>
              <div style={{ borderTop: '1px solid #e5e7eb', margin: '2px 0' }} />
              <label className="flex items-center gap-2 text-xs cursor-pointer select-none" title={t('viewAllFieldsHint')}>
                <input type="checkbox" checked={viewAllFields} onChange={onToggleViewAllFields} className="cursor-pointer" />
                {t('viewAllFields')}
              </label>
            </div>
          )}
        </>

        {/* Quick Connect toggle */}
        <div
          data-tour="toggle-connect"
          style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '4px 8px', borderRadius: '6px', backgroundColor: '#f3f4f6', border: connectActive ? '1px solid #16697A' : '1px solid #e5e7eb' }}
          title={connectActive ? t('quickConnectOnHint') : t('quickConnectOffHint')}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={connectActive ? '#16697A' : '#6b7280'} strokeWidth="2">
            <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
            <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
          </svg>
          <button
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); onToggleConnect(); }}
            style={{ position: 'relative', display: 'inline-flex', height: '18px', width: '32px', alignItems: 'center', borderRadius: '9px', backgroundColor: connectActive ? '#16697A' : '#d1d5db', border: 'none', cursor: 'pointer', transition: 'background-color 0.2s', padding: 0 }}
          >
            <span style={{ display: 'inline-block', height: '14px', width: '14px', borderRadius: '50%', backgroundColor: '#ffffff', transform: connectActive ? 'translateX(16px)' : 'translateX(2px)', transition: 'transform 0.2s', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }} />
          </button>
        </div>

        <button
          onClick={onShowPlanSettings}
          className="text-white px-2 py-1 rounded text-xs flex items-center gap-1 transition-all"
          style={{ background: 'linear-gradient(to bottom, #489FB5, #3a8fa5)', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}
          onMouseOver={(e) => { e.currentTarget.style.boxShadow = '0 6px 8px rgba(0,0,0,0.15)'; e.currentTarget.style.background = 'linear-gradient(to bottom, #3a8fa5, #2d7d91)'; }}
          onMouseOut={(e) => { e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)'; e.currentTarget.style.background = 'linear-gradient(to bottom, #489FB5, #3a8fa5)'; }}
        >
          ⚙ {t('planSettings')}
        </button>

        <button
          data-tour="btn-gantt"
          onClick={onShowGantt}
          className="text-white px-2 py-1 rounded text-xs flex items-center gap-1 transition-all"
          style={{ background: 'linear-gradient(to bottom, #FFA62B, #e8931a)', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}
          onMouseOver={(e) => { e.currentTarget.style.boxShadow = '0 6px 8px rgba(0,0,0,0.15)'; e.currentTarget.style.background = 'linear-gradient(to bottom, #e8931a, #d17d0f)'; }}
          onMouseOut={(e) => { e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)'; e.currentTarget.style.background = 'linear-gradient(to bottom, #FFA62B, #e8931a)'; }}
        >
          <BarChart3 size={13} />
          {t('gantt')}
        </button>

        {currentProjectId && (
          <button
            onClick={onShowProjectGantt}
            className="text-white px-2 py-1 rounded text-xs flex items-center gap-1 transition-all"
            style={{ background: 'linear-gradient(to bottom, #FFA62B, #e8931a)', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}
            onMouseOver={(e) => { e.currentTarget.style.boxShadow = '0 6px 8px rgba(0,0,0,0.15)'; e.currentTarget.style.background = 'linear-gradient(to bottom, #e8931a, #d17d0f)'; }}
            onMouseOut={(e) => { e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)'; e.currentTarget.style.background = 'linear-gradient(to bottom, #FFA62B, #e8931a)'; }}
            title={t('projectGanttTitle', { name: currentProjectName ?? '' })}
          >
            <BarChart3 size={13} />
            {t('project')}
          </button>
        )}

        <div className="h-6 w-px bg-gray-300"></div>

        <div
          data-tour="zoom-controls"
          className="flex items-center gap-1 border rounded px-2"
          style={{ background: 'linear-gradient(to bottom, #f9fafb, #f3f4f6)', boxShadow: '0 1px 2px rgba(0,0,0,0.05)', height: '28px' }}
        >
          <button
            onClick={onZoomOut}
            className="px-1 rounded transition-colors flex items-center"
            title={t('zoomOut')}
            style={{ background: 'transparent' }}
            onMouseOver={(e) => e.currentTarget.style.background = '#e5e7eb'}
            onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
          >
            <ZoomOut size={13} />
          </button>
          <span className="text-xs w-10 text-center font-medium">{Math.round(zoom * 100)}%</span>
          <button
            onClick={onZoomIn}
            className="px-1 rounded transition-colors flex items-center"
            title={t('zoomIn')}
            style={{ background: 'transparent' }}
            onMouseOver={(e) => e.currentTarget.style.background = '#e5e7eb'}
            onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
          >
            <ZoomIn size={13} />
          </button>
        </div>

        <LanguageSwitcher />
      </div>

    </div>
    </div>
  );
}
